/**
 * Netze product UI: availability beside price, order summary outside options box.
 */
(function () {
  const AVAILABILITY_SELECTOR = '[id^="net-length-availability-row-"]';
  const SLOT_SELECTOR = '[data-netze-availability-slot]';
  const ORDER_SUMMARY_SELECTOR = '[data-netze-order-summary]';
  const PRICE_DEBOUNCE_MS = 150;
  const PER_NET_MAX_M = 50;
  const LENGTH_MIN_M = 1.0;
  const NETZFINDER_PATH = '/pages/netzfinder';
  const MOBILE_MAX_WIDTH = '(max-width: 749px)';
  const MEDIA_AVAILABILITY_SLOT_SELECTOR = '[data-netze-media-availability-slot]';
  const MENGENRABATT_SLOT_SELECTOR = '[data-mengenrabatt-slot]';
  const MENGENRABATT_DISCOUNT_SELECTOR =
    '.onlyLengthDiscountInfoDiv, [id^="only-length-discount-info-"]';
  const STOCK_JSON_SELECTOR = '[id^="netze-variant-stock-"], [id^="netze-variant-lengths-"]';
  const STOCK_DISPLAY_SELECTOR = '[data-netze-variant-stock-display]';
  const STOCK_LOW_MAX_M = 10;
  const STOCK_GERING_MAX_M = 50;
  const KONFECTION_PROPERTY_KEYS = ['_b', '_c', '_d', '_e'];
  const KONFECTION_MIN_SQM = 20;

  const debouncedByRoot = new WeakMap();

  function debounceForRoot(root, fn) {
    let timers = debouncedByRoot.get(root);
    if (!timers) {
      timers = new Map();
      debouncedByRoot.set(root, timers);
    }
    return function debounced(key) {
      if (timers.has(key)) window.clearTimeout(timers.get(key));
      timers.set(
        key,
        window.setTimeout(() => {
          timers.delete(key);
          fn();
        }, PRICE_DEBOUNCE_MS)
      );
    };
  }

  const HEIGHT_CODE_MAP = {
    '00': null,
    '01': 1.0,
    '15': 1.5,
    '02': 2.0,
    '25': 2.5,
    '03': 3.0,
    '04': 4.0,
  };

  function parseLength(rawValue) {
    const normalized = String(rawValue || '')
      .trim()
      .replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function roundToOneDecimal(value) {
    return Math.round(Number(value) * 10) / 10;
  }

  function parseFixedHeightFromSku(sku) {
    const normalizedSku = String(sku || '').trim();
    if (normalizedSku.length < 5) return null;
    const heightCode = normalizedSku.slice(3, 5);
    return Object.prototype.hasOwnProperty.call(HEIGHT_CODE_MAP, heightCode)
      ? HEIGHT_CODE_MAP[heightCode]
      : null;
  }

  function getVariantIdInput(root) {
    const scope = root || document;
    const productForm = scope.querySelector('product-form');
    if (productForm) {
      const inProductForm = productForm.querySelector('input[name="id"], select[name="id"]');
      if (inProductForm) return inProductForm;
    }
    return scope.querySelector('input[name="id"], select[name="id"]');
  }

  function getSelectedVariantFromRoot(root) {
    const script = root.querySelector('variant-selects [data-selected-variant]');
    if (!script) return null;
    const raw = script.textContent.trim();
    if (!raw || raw === 'null' || raw === 'undefined') return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && parsed.id != null ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function isVariantSelectionValid(root, container) {
    const variantInput = getVariantIdInput(root);
    const variantId = variantInput ? String(variantInput.value || '').trim() : '';
    if (!variantId) return false;

    const selected = getSelectedVariantFromRoot(root);
    if (!selected || String(selected.id) !== variantId) return false;

    const variants = getVariantPrices(container);
    return variants.some((item) => String(item.id) === variantId);
  }

  function productHasFixedHeightVariants(container) {
    return getVariantPrices(container).some((variant) => {
      if (variant.fixedHeightM !== null && variant.fixedHeightM !== undefined && variant.fixedHeightM !== '') {
        return Number.isFinite(Number.parseFloat(variant.fixedHeightM));
      }
      return parseFixedHeightFromSku(variant.sku) !== null;
    });
  }

  function getVariantPrices(container) {
    const blockId = container.dataset.blockId;
    const script = document.getElementById(`net-variant-prices-${blockId}`);
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  function getCurrentVariant(container, root) {
    const variantInput = getVariantIdInput(root);
    const variantId = variantInput ? String(variantInput.value || '').trim() : '';
    if (!variantId) return null;

    const variants = getVariantPrices(container);
    return variants.find((item) => String(item.id) === variantId) || null;
  }

  function getFixedHeightForVariant(container, root) {
    const variant = getCurrentVariant(container, root);
    if (!variant) return null;

    if (variant.fixedHeightM !== null && variant.fixedHeightM !== undefined && variant.fixedHeightM !== '') {
      const parsed = Number.parseFloat(variant.fixedHeightM);
      if (Number.isFinite(parsed)) return roundToOneDecimal(parsed);
    }

    return parseFixedHeightFromSku(variant.sku);
  }

  function formatMeters(value) {
    return `${roundToOneDecimal(value).toFixed(1)} m`;
  }

  function stripAvailabilityLabel(row) {
    row.querySelectorAll(
      '.net-length-availability-label, .length-availability-label, [data-availability-label], .net-length-calculator-row-label, .net-length-row-label'
    ).forEach((el) => el.setAttribute('hidden', ''));

    Array.from(row.children).forEach((child) => {
      if (
        child.matches(
          '[class*="indicator"], [class*="status"], [class*="badge"], [class*="dot"], [class*="availability-value"], svg, [aria-hidden="true"]'
        )
      ) {
        return;
      }
      if (/^verfügbarkeit$/i.test(child.textContent.trim())) {
        child.setAttribute('hidden', '');
      }
    });

    const text = row.textContent.trim();
    if (/^verfügbarkeit\s+/i.test(text)) {
      const indicator = row.querySelector(
        '[class*="indicator"], [class*="status"], [class*="badge"], [class*="dot"], [class*="availability-value"]'
      );
      if (indicator) return;
      row.textContent = text.replace(/^verfügbarkeit\s+/i, '');
    }
  }

  function hideCalculatorSummary(root) {
    root.querySelectorAll('.netze-options-box .net-length-calculator-summary').forEach((summary) => {
      summary.setAttribute('hidden', '');
    });
  }

  function findMengenrabattDiscount(root) {
    return (
      root.querySelector(`.netze-options-box ${MENGENRABATT_DISCOUNT_SELECTOR}`) ||
      root.querySelector(MENGENRABATT_DISCOUNT_SELECTOR)
    );
  }

  function relocateMengenrabatt(root) {
    if (root.dataset.netzeRelocatingMengenrabatt === 'true') return;

    const slot = root.querySelector(MENGENRABATT_SLOT_SELECTOR);
    if (!slot) return;

    const discount = findMengenrabattDiscount(root);
    if (!discount) return;

    root.dataset.netzeRelocatingMengenrabatt = 'true';
    try {
      if (discount.parentElement !== slot) {
        slot.appendChild(discount);
      }
    } finally {
      root.dataset.netzeRelocatingMengenrabatt = 'false';
    }
  }

  function observeMengenrabatt(root) {
    if (root.dataset.netzeMengenrabattObserved === 'true') return;

    const slot = root.querySelector(MENGENRABATT_SLOT_SELECTOR);
    if (!slot) return;

    const scheduleRelocate = debounceForRoot(root, () => relocateMengenrabatt(root));
    const tryBind = () => {
      if (findMengenrabattDiscount(root)) {
        root.dataset.netzeMengenrabattObserved = 'true';
        scheduleRelocate('mengenrabatt');
      }
    };

    tryBind();
    if (root.dataset.netzeMengenrabattObserved === 'true') return;

    const optionsBox = root.querySelector('.netze-options-box');
    const watchTarget = optionsBox || root;
    const observer = new MutationObserver(() => tryBind());
    observer.observe(watchTarget, { childList: true, subtree: true });
  }

  function updateOrderSummaryDimensions(root) {
    const summary = root.querySelector(ORDER_SUMMARY_SELECTOR);
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!summary || !container) return;

    const blockId = container.dataset.blockId;
    const lengthInput = document.getElementById(`net-length-input-${blockId}`);
    const heightInput = document.getElementById(`net-height-input-${blockId}`);
    const requiresHeight = container.dataset.configuratorMode === 'full_config';

    const lengthEl = summary.querySelector('[data-netze-summary-length]');
    const heightEl = summary.querySelector('[data-netze-summary-height]');
    const heightRow = summary.querySelector('[data-netze-summary-height-row]');

    if (!isVariantSelectionValid(root, container)) {
      if (lengthEl) lengthEl.textContent = '–';
      if (heightEl) heightEl.textContent = '–';
      if (heightRow) {
        heightRow.hidden = !requiresHeight && !productHasFixedHeightVariants(container);
      }
      return;
    }

    const length = parseLength(lengthInput && lengthInput.value);
    if (lengthEl) {
      lengthEl.textContent = length !== null ? formatMeters(length) : '–';
    }

    let heightText = '–';
    let showHeightRow = true;

    if (requiresHeight && heightInput) {
      const height = parseLength(heightInput.value);
      heightText = height !== null ? formatMeters(height) : '–';
    } else {
      const fixedHeight = getFixedHeightForVariant(container, root);
      if (fixedHeight !== null) {
        heightText = formatMeters(fixedHeight);
      } else {
        showHeightRow = false;
      }
    }

    if (heightRow) heightRow.hidden = !showHeightRow;
    if (heightEl) heightEl.textContent = heightText;
    updateKonfektionNetCenter(root);
    updateOrderSummaryKonfektion(root);
  }

  function isKonfektionNetCenterEnhanced(center) {
    return Boolean(center.querySelector('.konfektion-net-center-dims'));
  }

  function ensureKonfektionNetCenterObserver(root, center) {
    if (center.dataset.netzeCenterObserved === 'true') return;

    center.dataset.netzeCenterObserved = 'true';
    const centerObserver = new MutationObserver(() => {
      if (center.dataset.netzeCenterUpdating === 'true') return;
      updateKonfektionNetCenter(root);
    });
    centerObserver.observe(center, { childList: true, characterData: true, subtree: true });
  }

  function scheduleKonfektionNetCenterRefresh(root) {
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!container) return;

    if (container._netzeCenterRefreshTimer) {
      window.clearTimeout(container._netzeCenterRefreshTimer);
    }

    container._netzeCenterRefreshTimer = window.setTimeout(() => {
      container._netzeCenterRefreshTimer = null;
      updateKonfektionNetCenter(root);
    }, 50);
  }

  function updateKonfektionNetCenter(root) {
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!container) return;

    const center = container.querySelector('.konfektionNetCenterDiv[data-konfektion-center]');
    if (!center) return;

    ensureKonfektionNetCenterObserver(root, center);

    const blockId = container.dataset.blockId;
    const lengthInput = document.getElementById(`net-length-input-${blockId}`);
    const heightInput = document.getElementById(`net-height-input-${blockId}`);
    const requiresHeight = container.dataset.configuratorMode === 'full_config';

    let lengthText = '–';
    let heightText = '–';
    let showHeight = true;

    if (isVariantSelectionValid(root, container)) {
      const length = parseLength(lengthInput && lengthInput.value);
      if (length !== null) lengthText = formatMeters(length);

      if (requiresHeight && heightInput) {
        const height = parseLength(heightInput.value);
        heightText = height !== null ? formatMeters(height) : '–';
      } else {
        const fixedHeight = getFixedHeightForVariant(container, root);
        if (fixedHeight !== null) {
          heightText = formatMeters(fixedHeight);
        } else {
          showHeight = false;
        }
      }
    }

    const nextKey = `${lengthText}|${heightText}|${showHeight}`;
    if (center.dataset.netzeCenterKey === nextKey && isKonfektionNetCenterEnhanced(center)) return;

    center.dataset.netzeCenterKey = nextKey;
    center.dataset.netzeCenterUpdating = 'true';

    try {
      let dims = center.querySelector('.konfektion-net-center-dims');
      if (!dims) {
        center.textContent = '';
        dims = document.createElement('div');
        dims.className = 'konfektion-net-center-dims';
        center.appendChild(dims);
      }

      dims.innerHTML = `
        <p class="konfektion-net-center-dim" data-netze-center-length>
          <span class="konfektion-net-center-dim-label">Länge</span>
          <span class="konfektion-net-center-dim-value">${lengthText}</span>
        </p>
        ${
          showHeight
            ? `<p class="konfektion-net-center-dim" data-netze-center-height>
          <span class="konfektion-net-center-dim-label">Höhe</span>
          <span class="konfektion-net-center-dim-value">${heightText}</span>
        </p>`
            : ''
        }
      `;
    } finally {
      delete center.dataset.netzeCenterUpdating;
    }
  }

  function getProductForm(root) {
    const productForm = root.querySelector('product-form');
    if (productForm) {
      const nestedForm = productForm.querySelector('form[action*="/cart/add"]');
      if (nestedForm) return nestedForm;
    }
    return root.querySelector('form[action*="/cart/add"]');
  }

  function getKonfektionPropertyLines(root) {
    const form = getProductForm(root);
    if (!form) return [];

    const lines = [];
    KONFECTION_PROPERTY_KEYS.forEach((key) => {
      const input = form.querySelector(
        `input[name="properties[${key}]"], textarea[name="properties[${key}]"]`
      );
      const value = input ? String(input.value || '').trim() : '';
      if (value) lines.push(value);
    });

    return lines;
  }

  function isMeaningfulKonfektionLine(line) {
    const text = String(line || '').trim();
    if (!text) return false;
    if (/^konfektion\s*konfigurieren/i.test(text)) return false;
    if (/^konfektion\s*\(?\s*aufpreis/i.test(text)) return false;

    const withoutLabel = text.replace(/^konfektion\s*[-–:]\s*/i, '').trim();
    if (/^konfektion$/i.test(text)) return false;
    if (!withoutLabel || /^[-–:·\s]+$/i.test(withoutLabel)) return false;

    return true;
  }

  function getKonfektionConfigLines(root, container) {
    const propertyLines = getKonfektionPropertyLines(root);
    const summaryData = readCalculatorSummaryKonfektion(container);
    const uiLines = getKonfektionConfigFromUi(container);

    return [...new Set([...propertyLines, ...summaryData.lines, ...uiLines])].filter(
      isMeaningfulKonfektionLine
    );
  }

  function parseAufpreisFromText(text) {
    const match = String(text || '').match(/Aufpreis\s*[:+]?\s*([\d.,]+)\s*€/i);
    return match ? `${match[1].trim()} €` : null;
  }

  function parseAufpreisFromKonfektionButton(button) {
    return button ? parseAufpreisFromText(button.textContent) : null;
  }

  function readCalculatorSummaryKonfektion(container) {
    const summary = container.querySelector('.net-length-calculator-summary');
    if (!summary) return { lines: [], aufpreis: null };

    const lines = [];
    const aufpreis = parseAufpreisFromText(summary.textContent);

    summary.querySelectorAll('.net-length-summary-row, tr, li, p, span, div').forEach((row) => {
      const rowText = row.textContent.trim();
      if (!rowText || rowText.length > 160) return;
      if (/^(höhe|länge|preis|gesamt|fläche)\s*:/i.test(rowText)) return;
      if (/konfektion|öse|kettel|umlauf|saum|rand|ösen/i.test(rowText)) {
        if (isMeaningfulKonfektionLine(rowText) && !lines.includes(rowText)) lines.push(rowText);
      }
    });

    return { lines, aufpreis };
  }

  function getKonfektionConfigFromUi(container) {
    const lines = [];
    const configRoot =
      container.querySelector('[id^="konfektion-config-"]') ||
      container.querySelector('.konfektion-config-panel');

    if (!configRoot) return lines;

    configRoot
      .querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked, select')
      .forEach((el) => {
        if (el.closest('[id^="konfektion-sqm-gate-"]')) return;

        let label = '';
        if (el.tagName === 'SELECT') {
          const option = el.options[el.selectedIndex];
          if (option && option.value) label = option.textContent.trim();
        } else {
          const parentLabel = el.closest('label');
          const linkedLabel = el.id ? configRoot.querySelector(`label[for="${el.id}"]`) : null;
          const labelEl = parentLabel || linkedLabel;
          if (labelEl) {
            label = labelEl.textContent.trim();
          } else if (el.value) {
            label = String(el.value).trim();
          }
        }

        if (label && !/^konfektion konfigurieren/i.test(label) && isMeaningfulKonfektionLine(label) && !lines.includes(label)) {
          lines.push(label);
        }
      });

    configRoot.querySelectorAll('[class*="konfektion"][class*="selected"], [data-konfektion-value]').forEach((el) => {
      const value = el.getAttribute('data-konfektion-value') || el.textContent.trim();
      if (value && value.length < 160 && isMeaningfulKonfektionLine(value) && !lines.includes(value)) {
        lines.push(value);
      }
    });

    return lines;
  }

  function getOrderAreaSqm(root, container) {
    const blockId = container.dataset.blockId;
    const lengthInput = document.getElementById(`net-length-input-${blockId}`);
    const heightInput = document.getElementById(`net-height-input-${blockId}`);
    const length = parseLength(lengthInput && lengthInput.value);

    let height = null;
    if (container.dataset.configuratorMode === 'full_config' && heightInput) {
      height = parseLength(heightInput.value);
    } else {
      const fixedHeight = getFixedHeightForVariant(container, root);
      if (fixedHeight !== null) height = fixedHeight;
    }

    if (length === null || height === null) return null;
    return roundToOneDecimal(length * height);
  }

  function isBelowKonfektionMinSqm(root, container) {
    const areaSqm = getOrderAreaSqm(root, container);
    if (areaSqm !== null) return areaSqm < KONFECTION_MIN_SQM;

    const gate = container.querySelector('[id^="konfektion-sqm-gate-"]');
    if (!gate) return false;
    return !(gate.hasAttribute('hidden') || gate.hidden);
  }

  function isKonfektionOptInButtonActive(button) {
    if (!button) return false;
    const text = button.textContent.toLowerCase();
    if (/entfernen|deaktivieren|bearbeiten|ändern|aktiv/.test(text)) return true;
    if (button.getAttribute('aria-pressed') === 'true') return true;
    return button.classList.contains('is-active') || button.classList.contains('active');
  }

  function isKonfektionUserAdded(root, container) {
    if (container.dataset.netzeKonfektionOptedIn === 'true') return true;
    if (getKonfektionConfigLines(root, container).length > 0) return true;

    const optInBtn = container.querySelector('[id^="konfektion-opt-in-btn-"]');
    if (isKonfektionOptInButtonActive(optInBtn)) return true;

    return (
      container.dataset.konfektionEnabled === 'true' || container.dataset.konfektionActive === 'true'
    );
  }

  function maybeClearKonfektionOptIn(root, container, configLines) {
    if (container.dataset.netzeKonfektionOptedIn !== 'true') return;

    if (!isBelowKonfektionMinSqm(root, container)) {
      container.dataset.netzeKonfektionOptedIn = 'false';
      return;
    }

    if (container.dataset.netzeKonfektionJustClicked === 'true') return;
    if (configLines.length > 0) return;
    if (container.dataset.konfektionEnabled === 'true' || container.dataset.konfektionActive === 'true') {
      return;
    }

    const optInBtn = container.querySelector('[id^="konfektion-opt-in-btn-"]');
    if (isKonfektionOptInButtonActive(optInBtn)) return;
    if (!optInBtn || !/^konfektion\s*konfigurieren/i.test(optInBtn.textContent.trim())) return;

    const gate = container.querySelector('[id^="konfektion-sqm-gate-"]');
    if (!gate || gate.hasAttribute('hidden') || gate.hidden) return;

    container.dataset.netzeKonfektionOptedIn = 'false';
  }

  function updateOrderSummaryKonfektion(root) {
    const summary = root.querySelector(ORDER_SUMMARY_SELECTOR);
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!summary || !container) return;

    const block = summary.querySelector('[data-netze-summary-konfektion-block]');
    const priceRow = summary.querySelector('[data-netze-summary-konfektion-price-row]');
    const aufpreisEl = summary.querySelector('[data-netze-summary-aufpreis]');
    const listEl = summary.querySelector('[data-netze-summary-konfektion-list]');
    if (!block || !priceRow || !aufpreisEl || !listEl) return;

    const configLines = getKonfektionConfigLines(root, container);
    maybeClearKonfektionOptIn(root, container, configLines);

    const summaryData = readCalculatorSummaryKonfektion(container);
    const optInBtn = container.querySelector('[id^="konfektion-opt-in-btn-"]');
    const userAdded = isKonfektionUserAdded(root, container);
    const belowMinSqm = isBelowKonfektionMinSqm(root, container);
    const aufpreisValue =
      summaryData.aufpreis ||
      container._netzeKonfektionAufpreis ||
      parseAufpreisFromKonfektionButton(optInBtn) ||
      null;
    const showAufpreis = Boolean(userAdded && belowMinSqm && aufpreisValue);
    const hasConfig = configLines.length > 0;

    if (!userAdded || (!showAufpreis && !hasConfig)) {
      block.hidden = true;
      priceRow.hidden = true;
      listEl.hidden = true;
      listEl.replaceChildren();
      aufpreisEl.textContent = '';
      return;
    }

    block.hidden = false;
    priceRow.hidden = !showAufpreis;
    aufpreisEl.textContent = showAufpreis ? `+ ${aufpreisValue}` : '';

    listEl.replaceChildren();
    if (hasConfig) {
      configLines.forEach((line) => {
        const item = document.createElement('li');
        item.className = 'netze-order-summary__konfektion-item';
        item.textContent = line;
        listEl.appendChild(item);
      });
      listEl.hidden = false;
    } else {
      listEl.hidden = true;
    }
  }

  function bindKonfektionSummary(root, container) {
    if (!container || container.dataset.netzeKonfektionSummaryBound === 'true') return;

    container.dataset.netzeKonfektionSummaryBound = 'true';

    const optInBtn = container.querySelector('[id^="konfektion-opt-in-btn-"]');
    if (optInBtn) {
      container._netzeKonfektionAufpreis = parseAufpreisFromKonfektionButton(optInBtn);
      optInBtn.addEventListener('click', () => {
        if (!isBelowKonfektionMinSqm(root, container)) return;

        container.dataset.netzeKonfektionOptedIn = 'true';
        container.dataset.netzeKonfektionJustClicked = 'true';
        if (!container._netzeKonfektionAufpreis) {
          container._netzeKonfektionAufpreis = parseAufpreisFromKonfektionButton(optInBtn);
        }

        updateOrderSummaryKonfektion(root);
        updateOrderSummaryPrice(root);

        window.setTimeout(() => {
          updateOrderSummaryKonfektion(root);
          updateOrderSummaryPrice(root);
        }, 80);

        window.setTimeout(() => {
          updateOrderSummaryKonfektion(root);
          updateOrderSummaryPrice(root);
        }, 350);

        window.setTimeout(() => {
          delete container.dataset.netzeKonfektionJustClicked;
        }, 600);
      });
    }

    const form = getProductForm(root);
    if (form) {
      const onKonfektionFormChange = (event) => {
        const target = event.target;
        if (!target || !target.name) return;
        if (
          target.name.startsWith('properties[') ||
          target.closest('[id^="net-length-calculator-container-"]')
        ) {
          updateOrderSummaryKonfektion(root);
          scheduleKonfektionNetCenterRefresh(root);
        }
      };
      form.addEventListener('change', onKonfektionFormChange);
      form.addEventListener('input', onKonfektionFormChange);
    }

    const onKonfektionUiChange = (event) => {
      if (
        event.target &&
        (event.target.matches('.konfektion-side-select') ||
          event.target.closest('[id^="konfektion-picker-"]'))
      ) {
        scheduleKonfektionNetCenterRefresh(root);
      }
    };

    container.addEventListener('change', onKonfektionUiChange);
    container.addEventListener('input', onKonfektionUiChange);
    container.addEventListener('change', () => updateOrderSummaryKonfektion(root));
    container.addEventListener('input', () => updateOrderSummaryKonfektion(root));
  }

  function updateOrderSummaryPrice(root) {
    const summary = root.querySelector(ORDER_SUMMARY_SELECTOR);
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!summary || !container) return;

    const blockId = container.dataset.blockId;
    const totalDisplay = document.getElementById(`net-total-price-display-${blockId}`);
    const priceEl = summary.querySelector('[data-netze-summary-price]');

    if (!priceEl) return;

    if (!isVariantSelectionValid(root, container)) {
      priceEl.textContent = '–';
      return;
    }

    const totalText = totalDisplay ? totalDisplay.textContent.trim() : '';
    priceEl.textContent = totalText && totalText !== '-' ? totalText : '–';
  }

  function updateOrderSummary(root) {
    updateOrderSummaryDimensions(root);
    updateOrderSummaryKonfektion(root);
    updateOrderSummaryPrice(root);
  }

  function bindOrderSummary(root) {
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!container || container.dataset.netzeOrderSummaryBound === 'true') return;

    container.dataset.netzeOrderSummaryBound = 'true';
    hideCalculatorSummary(root);
    bindKonfektionSummary(root, container);

    const blockId = container.dataset.blockId;
    const totalDisplay = document.getElementById(`net-total-price-display-${blockId}`);
    const schedulePriceUpdate = debounceForRoot(root, () => updateOrderSummaryPrice(root));

    const onDimensionChange = (event) => {
      if (
        !event.target.matches('input[id^="net-length-input-"], input[id^="net-height-input-"]')
      ) {
        return;
      }
      updateOrderSummaryDimensions(root);
      updateAvailabilityLengthDisplay(root);
      schedulePriceUpdate('price');
    };

    container.addEventListener('input', onDimensionChange);
    container.addEventListener('change', onDimensionChange);
    container.addEventListener('blur', onDimensionChange, true);

    if (totalDisplay) {
      const observer = new MutationObserver(() => schedulePriceUpdate('price'));
      observer.observe(totalDisplay, { childList: true, characterData: true, subtree: true });
    }

    const variantInput = getVariantIdInput(root);
    if (variantInput) {
      variantInput.addEventListener('change', () => {
        updateOrderSummary(root);
        updateAvailabilityLengthDisplay(root);
        updateLengthInputLimits(root);
      });
    }

    updateOrderSummary(root);
    updateLengthInputLimits(root);
  }

  function rememberRuntimeVariantStock(root, variantId, meters) {
    if (variantId == null || meters == null) return;
    if (!root._netzeRuntimeStockCache) root._netzeRuntimeStockCache = Object.create(null);
    root._netzeRuntimeStockCache[String(variantId)] = meters;
  }

  function getAvailableLengthFromNetVariantPrices(root, variantId) {
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!container) return null;

    const blockId = container.dataset.blockId;
    const script = document.getElementById(`net-variant-prices-${blockId}`);
    if (!script) return null;

    try {
      const variants = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(variants)) return null;

      const variant = variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return null;

      const candidates = [variant.availableLengthM, variant.available_length_m];

      for (const candidate of candidates) {
        const parsed = parseVariantAvailableLength(candidate);
        if (parsed !== null) return parsed;
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function loadVariantStockCache(root) {
    if (root._netzeVariantStockCache) return root._netzeVariantStockCache;

    const script = root.querySelector(STOCK_JSON_SELECTOR);
    if (!script) {
      root._netzeVariantStockCache = Object.freeze({});
      return root._netzeVariantStockCache;
    }

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      root._netzeVariantStockCache = Object.freeze(parsed && typeof parsed === 'object' ? parsed : {});
    } catch (error) {
      root._netzeVariantStockCache = Object.freeze({});
    }

    return root._netzeVariantStockCache;
  }

  function parseVariantAvailableLength(rawValue) {
    if (rawValue == null || rawValue === '') return null;
    const parsed = Number.parseFloat(String(rawValue).trim().replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? roundToOneDecimal(parsed) : null;
  }

  function getSelectedVariantId(root) {
    const selected = getSelectedVariantFromRoot(root);
    if (selected?.id != null) return String(selected.id);

    const variantInput = getVariantIdInput(root);
    return variantInput ? String(variantInput.value || '').trim() : '';
  }

  function getVariantAvailableLength(root, variantId) {
    const id = variantId || getSelectedVariantId(root);
    if (!id) return null;

    if (root._netzeRuntimeStockCache && root._netzeRuntimeStockCache[id] != null) {
      return root._netzeRuntimeStockCache[id];
    }

    const fromStockJson = parseVariantAvailableLength(loadVariantStockCache(root)[id]);
    if (fromStockJson !== null) {
      rememberRuntimeVariantStock(root, id, fromStockJson);
      return fromStockJson;
    }

    const fromAppPrices = getAvailableLengthFromNetVariantPrices(root, id);
    if (fromAppPrices !== null) {
      rememberRuntimeVariantStock(root, id, fromAppPrices);
      return fromAppPrices;
    }

    return null;
  }

  function getEffectiveMaxLength(root, variantId) {
    const available = getVariantAvailableLength(root, variantId);
    if (available === null) return PER_NET_MAX_M;
    return Math.min(PER_NET_MAX_M, available);
  }

  function formatAvailabilityMeters(value) {
    const rounded = roundToOneDecimal(value);
    return Number.isInteger(rounded) ? `${rounded} m` : `${rounded.toFixed(1)} m`;
  }

  function getStockIndicator(meters) {
    if (meters == null) return null;

    const value = roundToOneDecimal(meters);
    if (value < STOCK_LOW_MAX_M) {
      return { meters: value, label: 'Low', level: 'low' };
    }
    if (value < STOCK_GERING_MAX_M) {
      return { meters: value, label: 'Gering', level: 'gering' };
    }
    return { meters: value, label: 'On stock', level: 'in-stock' };
  }

  function applyStockLevelClasses(container, indicator) {
    if (!container) return;
    container.classList.remove(
      'netze-stock-indicator--low',
      'netze-stock-indicator--gering',
      'netze-stock-indicator--in-stock'
    );
    if (indicator) container.classList.add(`netze-stock-indicator--${indicator.level}`);
  }

  function applyStockIndicatorToElement(el, indicator) {
    if (!el) return;

    el.classList.add('netze-stock-indicator');
    applyStockLevelClasses(el, indicator);

    if (!indicator) {
      el.textContent = '-';
      el.removeAttribute('data-netze-stock-level');
      el.removeAttribute('title');
      return;
    }

    el.setAttribute('data-netze-stock-level', indicator.level);
    el.setAttribute('title', formatAvailabilityMeters(indicator.meters));

    let dot = el.querySelector(':scope > .netze-stock-indicator-dot');
    let label = el.querySelector(':scope > .netze-stock-indicator-label');

    if (!dot || !label) {
      el.textContent = '';
      dot = document.createElement('span');
      dot.className = 'netze-stock-indicator-dot';
      dot.setAttribute('aria-hidden', 'true');
      label = document.createElement('span');
      label.className = 'netze-stock-indicator-label';
      el.append(dot, label);
    }

    label.textContent = indicator.label;
  }

  function hideAppStockChrome(row) {
    if (!row) return;

    row.querySelectorAll(
      '[class*="indicator"]:not(.netze-stock-indicator-dot), [class*="status"]:not(.netze-stock-indicator-label), [class*="badge"], [class*="dot"]:not(.netze-stock-indicator-dot), [class*="availability-value"], svg'
    ).forEach((el) => {
      if (el.classList.contains('netze-stock-indicator-dot')) return;
      el.setAttribute('hidden', '');
    });
  }

  function isGermanLocale() {
    const lang = (document.documentElement.lang || '').toLowerCase();
    return lang.startsWith('de');
  }

  function getLengthLimitMessages() {
    if (isGermanLocale()) {
      return {
        lowStock: {
          message: 'Mehr ist leider nicht verfügbar zurzeit.',
          linkText: 'Zum Netzfinder',
          linkHref: NETZFINDER_PATH,
        },
        perNetMax: {
          message: 'Maximal 50 Meter pro Netz möglich!',
        },
      };
    }
    return {
      lowStock: {
        message: 'More is unfortunately not available at the moment.',
        linkText: 'Go to Netzfinder',
        linkHref: NETZFINDER_PATH,
      },
      perNetMax: {
        message: 'Maximum 50 meters per net allowed!',
      },
    };
  }

  function getLengthFeedbackContent(root, value) {
    const parsed = Number.parseFloat(String(value).trim().replace(',', '.'));
    if (!Number.isFinite(parsed)) return null;

    const messages = getLengthLimitMessages();
    if (parsed > PER_NET_MAX_M) {
      return { ...messages.perNetMax, alertType: 'per-net-max' };
    }

    const available = getVariantAvailableLength(root);
    if (available !== null && parsed > available) {
      return { ...messages.lowStock, alertType: 'unavailable' };
    }

    return null;
  }

  function clampLengthValue(root, value) {
    const parsed = Number.parseFloat(String(value).trim().replace(',', '.'));
    if (!Number.isFinite(parsed)) return LENGTH_MIN_M;

    let clamped = parsed;
    if (clamped < LENGTH_MIN_M) clamped = LENGTH_MIN_M;
    if (clamped > PER_NET_MAX_M) clamped = PER_NET_MAX_M;

    const effectiveMax = getEffectiveMaxLength(root);
    if (clamped > effectiveMax) clamped = effectiveMax;

    return roundToOneDecimal(clamped);
  }

  function updateLengthInputLimits(root) {
    const container = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!container) return;

    const blockId = container.dataset.blockId;
    const lengthInput = document.getElementById(`net-length-input-${blockId}`);
    if (!lengthInput) return;
    if (document.activeElement === lengthInput) return;

    const effectiveMax = getEffectiveMaxLength(root);
    lengthInput.min = String(LENGTH_MIN_M);
    lengthInput.max = String(PER_NET_MAX_M);
    lengthInput.title = `max ${formatAvailabilityMeters(effectiveMax)}`;

    const clamped = clampLengthValue(root, lengthInput.value);
    const formatted = clamped.toFixed(1);
    const nextValue = (lengthInput.value || '').includes(',') ? formatted.replace('.', ',') : formatted;

    if (lengthInput.value !== nextValue) {
      lengthInput.value = nextValue;
      lengthInput.dataset.netzeCommittedValue = nextValue;
      lengthInput.dispatchEvent(new Event('input', { bubbles: true }));
      lengthInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (window.NetzeStepperTouch && typeof window.NetzeStepperTouch.refreshCalculator === 'function') {
      window.NetzeStepperTouch.refreshCalculator(container);
    }
  }

  function hideInBoxAvailabilityRow(row) {
    if (!row) return;
    row.setAttribute('hidden', '');
    row.hidden = true;
    row.setAttribute('aria-hidden', 'true');
  }

  function isAvailabilityRowInOptionsBox(root, row) {
    return Boolean(row?.closest('.netze-options-box'));
  }

  function setStockIndicatorDisplay(root, indicator) {
    const row = root.querySelector(AVAILABILITY_SELECTOR);
    const mobile = isMobileNetzeLayout();
    const rowInOptionsBox = isAvailabilityRowInOptionsBox(root, row);

    if (row && !(mobile && rowInOptionsBox)) {
      row.dataset.netzeStockApplying = 'true';
      hideAppStockChrome(row);
      applyStockLevelClasses(row, indicator);

      const textEl = row.querySelector('[id^="net-length-availability-text-"]');
      const contentEl = row.querySelector('.length-availability-content');
      if (contentEl) applyStockIndicatorToElement(contentEl, indicator);
      else if (textEl) applyStockIndicatorToElement(textEl, indicator);
      else applyStockIndicatorToElement(row, indicator);

      window.requestAnimationFrame(() => {
        delete row.dataset.netzeStockApplying;
      });
    } else if (row && mobile && rowInOptionsBox) {
      hideInBoxAvailabilityRow(row);
    }

    if (isMobileNetzeLayout()) {
      const slot = getMediaAvailabilitySlot(root);
      if (!slot) return;

      if (!indicator) {
        slot.setAttribute('hidden', '');
        const display = slot.querySelector(STOCK_DISPLAY_SELECTOR);
        if (display) display.setAttribute('hidden', '');
        return;
      }

      slot.removeAttribute('hidden');
      const display = ensureMobileStockDisplay(root);
      if (display) {
        applyStockLevelClasses(display, indicator);
        applyStockIndicatorToElement(display, indicator);
        display.removeAttribute('hidden');
      }
      return;
    }

    const preload = root.querySelector(STOCK_DISPLAY_SELECTOR);
    if (preload) {
      applyStockLevelClasses(preload, indicator);
      applyStockIndicatorToElement(preload, indicator);
    }
  }

  function updateAvailabilityLengthDisplay(root, variantId) {
    const id = variantId != null ? String(variantId) : getSelectedVariantId(root);

    if (!id) {
      setStockIndicatorDisplay(root, null);
      return;
    }

    const available = getVariantAvailableLength(root, id);
    setStockIndicatorDisplay(root, available !== null ? getStockIndicator(available) : null);
  }

  function syncVariantStockUi(root, variantId) {
    updateAvailabilityLengthDisplay(root, variantId);
    updateLengthInputLimits(root);
    relocateNetzeAvailability(root);
  }

  function bindVariantStockSync(root) {
    if (root.dataset.netzeStockSyncBound === 'true') return;

    loadVariantStockCache(root);

    root.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="id"], select[name="id"]')) return;
      syncVariantStockUi(root);
    });

    root.addEventListener('change', (event) => {
      if (!event.target.closest('variant-selects')) return;
      requestAnimationFrame(() => syncVariantStockUi(root));
    });

    root.dataset.netzeStockSyncBound = 'true';
  }

  function isMobileNetzeLayout() {
    return window.matchMedia(MOBILE_MAX_WIDTH).matches;
  }

  function getMediaAvailabilitySlot(root) {
    const product = root.querySelector('.product') || root.closest('.product');
    const mediaWrapper = product?.querySelector('.product__media-wrapper');
    if (!mediaWrapper) return null;

    let slot = mediaWrapper.querySelector(MEDIA_AVAILABILITY_SLOT_SELECTOR);
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'netze-media-availability';
      slot.setAttribute('data-netze-media-availability-slot', '');
      mediaWrapper.appendChild(slot);
    }

    return slot;
  }

  function ensureMobileStockDisplay(root) {
    const slot = getMediaAvailabilitySlot(root);
    if (!slot) return null;

    slot.removeAttribute('hidden');

    let display = slot.querySelector(STOCK_DISPLAY_SELECTOR);
    if (!display) {
      display = document.createElement('p');
      display.className = 'netze-variant-stock-display';
      display.setAttribute('data-netze-variant-stock-display', '');
      slot.appendChild(display);
    }

    return display;
  }

  function showAvailabilityRow(row) {
    if (row.hasAttribute('hidden')) row.removeAttribute('hidden');
    if (row.hidden) row.hidden = false;
  }

  function relocateNetzeAvailability(root) {
    if (root.dataset.netzeRelocatingAvailability === 'true') return;

    root.dataset.netzeRelocatingAvailability = 'true';

    try {
      updateAvailabilityLengthDisplay(root);

      if (isMobileNetzeLayout()) {
        hideInBoxAvailabilityRow(root.querySelector(AVAILABILITY_SELECTOR));
        ensureMobileStockDisplay(root);
        return;
      }

      const inline = root.querySelector('.netze-price-inline');
      const row = root.querySelector(AVAILABILITY_SELECTOR);
      if (!row || !inline) return;

      stripAvailabilityLabel(row);
      showAvailabilityRow(row);
      if (row.parentElement !== inline) inline.appendChild(row);
    } finally {
      root.dataset.netzeRelocatingAvailability = 'false';
    }
  }

  function bindAvailabilityLayout(root) {
    if (root.dataset.netzeAvailabilityLayoutBound === 'true') return;
    root.dataset.netzeAvailabilityLayoutBound = 'true';

    const scheduleRelocate = debounceForRoot(root, () => relocateNetzeAvailability(root));
    const mobileQuery = window.matchMedia(MOBILE_MAX_WIDTH);
    const onLayoutChange = () => scheduleRelocate('layout');

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onLayoutChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(onLayoutChange);
    }

    window.addEventListener('resize', onLayoutChange, { passive: true });
  }

  function observeCalculator(root) {
    if (root.dataset.netzeAvailabilityObserved === 'true') return;
    const calculator = root.querySelector('[id^="net-length-calculator-container-"]');
    if (!calculator) return;

    root.dataset.netzeAvailabilityObserved = 'true';
    const scheduleCalculatorPriceUpdate = debounceForRoot(root, () => {
      hideCalculatorSummary(root);
      updateAvailabilityLengthDisplay(root);
      relocateNetzeAvailability(root);
      relocateMengenrabatt(root);
      updateOrderSummaryPrice(root);
    });

    const observer = new MutationObserver(() => {
      updateOrderSummaryDimensions(root);
      updateKonfektionNetCenter(root);
      updateOrderSummaryKonfektion(root);
      scheduleCalculatorPriceUpdate('calculator');
    });
    observer.observe(calculator, { childList: true, subtree: true });

    observeNetconsultAvailabilityRow(root, calculator);
  }

  function observeNetconsultAvailabilityRow(root, calculator) {
    if (root.dataset.netzeNetconsultRowObserved === 'true') return;

    const bindRow = (row) => {
      if (!row || row.dataset.netzeNetconsultRowBound === 'true') return;
      row.dataset.netzeNetconsultRowBound = 'true';

      const reapplyFromMetafield = debounceForRoot(root, () => {
        if (isMobileNetzeLayout() && isAvailabilityRowInOptionsBox(root, row)) {
          hideInBoxAvailabilityRow(row);
          return;
        }
        updateAvailabilityLengthDisplay(root);
        updateLengthInputLimits(root);
      });

      const rowObserver = new MutationObserver(() => {
        if (row.dataset.netzeStockApplying === 'true') return;
        if (isMobileNetzeLayout() && isAvailabilityRowInOptionsBox(root, row)) {
          hideInBoxAvailabilityRow(row);
          return;
        }
        reapplyFromMetafield('netconsult-row');
      });
      rowObserver.observe(row, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      updateAvailabilityLengthDisplay(root);
    };

    const existingRow = root.querySelector(AVAILABILITY_SELECTOR);
    if (existingRow) {
      root.dataset.netzeNetconsultRowObserved = 'true';
      bindRow(existingRow);
      return;
    }

    if (!calculator) return;

    const watchObserver = new MutationObserver(() => {
      const row = root.querySelector(AVAILABILITY_SELECTOR);
      if (!row) return;
      root.dataset.netzeNetconsultRowObserved = 'true';
      bindRow(row);
      watchObserver.disconnect();
    });
    watchObserver.observe(calculator, { childList: true, subtree: true });
  }

  function isNetzeProduct(root) {
    return (
      root.classList.contains('product-info--netze') ||
      Boolean(root.querySelector(STOCK_JSON_SELECTOR)) ||
      Boolean(root.querySelector(MEDIA_AVAILABILITY_SLOT_SELECTOR))
    );
  }

  function initNetzeProductUi() {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      if (!isNetzeProduct(productInfo)) return;
      loadVariantStockCache(productInfo);
      bindVariantStockSync(productInfo);
      hideCalculatorSummary(productInfo);
      bindOrderSummary(productInfo);
      bindAvailabilityLayout(productInfo);
      syncVariantStockUi(productInfo);
      relocateMengenrabatt(productInfo);
      observeMengenrabatt(productInfo);
      observeCalculator(productInfo);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNetzeProductUi);
  } else {
    initNetzeProductUi();
  }

  window.NetzeLengthLimits = {
    PER_NET_MAX_M,
    LENGTH_MIN_M,
    STOCK_LOW_MAX_M,
    STOCK_GERING_MAX_M,
    getVariantAvailableLength,
    getStockIndicator,
    getEffectiveMaxLength,
    clampLengthValue,
    getLengthLimitMessages,
    getLengthFeedbackContent,
    NETZFINDER_PATH,
    updateLengthInputLimits,
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variantId = event?.data?.variant?.id;

      document.querySelectorAll('product-info').forEach((productInfo) => {
        if (!isNetzeProduct(productInfo)) return;
        syncVariantStockUi(productInfo, variantId);
        if (productInfo.querySelector(ORDER_SUMMARY_SELECTOR)) {
          updateOrderSummary(productInfo);
        }
      });

      requestAnimationFrame(initNetzeProductUi);
    });
  }
})();
